import axios from 'axios';
import { hasProAccess, getCurrentUser, loginUser } from './auth';

const LINKEDIN_CLIENT_ID = import.meta.env.VITE_LINKEDIN_CLIENT_ID;
const LINKEDIN_REDIRECT_URI = import.meta.env.VITE_LINKEDIN_REDIRECT_URI;

// Store LinkedIn state in localStorage to prevent CSRF attacks
const generateState = () => {
  const state = Math.random().toString(36).substring(7);
  localStorage.setItem('linkedin_state', state);
  return state;
};

// Get user profile from userinfo endpoint
const getLinkedInUserProfile = async (accessToken: string) => {
  try {
    const response = await axios.get('https://api.linkedin.com/v2/userinfo', {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération du profil LinkedIn:', error);
    throw error;
  }
};

export const initiateLinkedInLogin = async () => {
  if (!LINKEDIN_CLIENT_ID || !LINKEDIN_REDIRECT_URI) {
    throw new Error('Configuration LinkedIn manquante');
  }

  // Generate and store state
  const state = generateState();
  
  // Define required scope - use openid, profile and email
  const scope = encodeURIComponent('openid profile email');
  
  // Construct the authorization URL with proper encoding
  const authUrl = `https://www.linkedin.com/oauth/v2/authorization?` +
    `response_type=code` +
    `&client_id=${encodeURIComponent(LINKEDIN_CLIENT_ID)}` +
    `&redirect_uri=${encodeURIComponent(LINKEDIN_REDIRECT_URI)}` +
    `&state=${encodeURIComponent(state)}` +
    `&scope=${scope}`;

  // Redirect to LinkedIn authorization page
  window.location.href = authUrl;
};

export const handleLinkedInCallback = async (code: string, state: string) => {
  const savedState = localStorage.getItem('linkedin_state');
  if (state !== savedState) {
    throw new Error('État LinkedIn invalide');
  }

  try {
    // Exchange code for access token
    const formData = new URLSearchParams();
    formData.append('grant_type', 'authorization_code');
    formData.append('code', code);
    formData.append('redirect_uri', LINKEDIN_REDIRECT_URI);
    formData.append('client_id', LINKEDIN_CLIENT_ID);

    const tokenResponse = await axios.post(
      'https://www.linkedin.com/oauth/v2/accessToken',
      formData.toString(),
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      }
    );

    const { access_token, expires_in } = tokenResponse.data;
    
    // Store token securely
    localStorage.setItem('linkedin_token', access_token);
    localStorage.setItem('linkedin_token_expiry', (Date.now() + expires_in * 1000).toString());

    // Get user profile from userinfo endpoint
    const userProfile = await getLinkedInUserProfile(access_token);
    
    // Extract user information
    const { sub: linkedInId, email, given_name, family_name } = userProfile;
    
    // Use email from userinfo or generate one from LinkedIn ID
    const userEmail = email || `${linkedInId}@linkedin.com`;
    const userName = `${given_name} ${family_name}`;

    // Login user with LinkedIn credentials
    return await loginUser(userEmail, 'linkedin-auth');
  } catch (error: any) {
    console.error('LinkedIn authentication error:', error);
    if (error.response) {
      console.error('LinkedIn API response:', error.response.data);
    }
    throw new Error('Erreur lors de l\'authentification LinkedIn');
  }
};

export const shareOnLinkedIn = async (content: string) => {
  const user = getCurrentUser();
  if (!user) throw new Error('Utilisateur non authentifié');

  const token = localStorage.getItem('linkedin_token');
  if (!token) {
    throw new Error('Non connecté à LinkedIn');
  }

  // Check token expiry
  const expiry = localStorage.getItem('linkedin_token_expiry');
  if (expiry && Date.now() >= parseInt(expiry, 10)) {
    localStorage.removeItem('linkedin_token');
    localStorage.removeItem('linkedin_token_expiry');
    throw new Error('Session LinkedIn expirée. Veuillez vous reconnecter.');
  }
  
  try {
    // Get user profile to get the person URN
    const profileResponse = await axios.get('https://api.linkedin.com/v2/me', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    const personId = profileResponse.data.id;

    // Add required hashtags
    const formattedContent = `${content}\n\n#recruteurs #offresgratuites`;
    
    // Share post via LinkedIn API
    const response = await axios.post(
      'https://api.linkedin.com/v2/ugcPosts',
      {
        author: `urn:li:person:${personId}`,
        lifecycleState: 'PUBLISHED',
        specificContent: {
          'com.linkedin.ugc.ShareContent': {
            shareCommentary: {
              text: formattedContent
            },
            shareMediaCategory: 'NONE'
          }
        },
        visibility: {
          'com.linkedin.ugc.MemberNetworkVisibility': 'PUBLIC'
        }
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Restli-Protocol-Version': '2.0.0'
        }
      }
    );
    
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 401) {
      // Clear invalid token
      localStorage.removeItem('linkedin_token');
      localStorage.removeItem('linkedin_token_expiry');
      throw new Error('Session LinkedIn expirée. Veuillez vous reconnecter.');
    }
    console.error('LinkedIn sharing error:', error);
    throw new Error('Erreur lors du partage sur LinkedIn. Veuillez réessayer.');
  }
};

export const isLinkedInAuthenticated = (): boolean => {
  const token = localStorage.getItem('linkedin_token');
  const expiry = localStorage.getItem('linkedin_token_expiry');
  
  if (!token || !expiry) {
    return false;
  }
  
  // Check if token has expired
  const expiryTime = parseInt(expiry, 10);
  if (Date.now() >= expiryTime) {
    // Clear expired token
    localStorage.removeItem('linkedin_token');
    localStorage.removeItem('linkedin_token_expiry');
    return false;
  }
  
  return true;
};